/**
 * IB Management Dashboard — Step 3 of Scorecard Phase 2.
 *
 * Grid view of ALL CRM channels with status, owner, achievement %.
 * Filter by status / channel type / owner / linked-status. Click row → drill to that channel's dashboard.
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Building2, RefreshCw, Search, Filter, ChevronRight, AlertCircle,
  CheckCircle2, AlertTriangle, TrendingDown, HelpCircle, Link2Off, Tv
} from 'lucide-react';
import {
  StatusBadge, ProgressBar, PeriodSelector, STATUS_META, Sparkline, MiniHBar, StatusDonut
} from '@/components/scorecard/ScorecardPrimitives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SummaryCard({ icon: Icon, label, value, total, color, status }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className={`bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-4 hover:border-[hsl(var(--border-hover))] transition cursor-pointer`}
      data-testid={`summary-card-${status || label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
          <Icon size={14} style={{ color }} />
        </div>
        <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">{label}</p>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-white">{value}</span>
        {total > 0 && <span className="text-xs text-[#6B7280]">/ {total}</span>}
      </div>
      {total > 0 && (
        <div className="mt-2 w-full bg-[hsl(var(--surface-elevated))] rounded-full h-1 overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
      )}
    </div>
  );
}

export default function IBManagementDashboardPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoSwitched, setAutoSwitched] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [linkedFilter, setLinkedFilter] = useState('linked');

  const fetchOverview = async (p = period) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [r, t] = await Promise.all([
        axios.get(`${API}/scorecard/ib-management/overview?period=${p}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/scorecard/trend?scope=ib_management&months=6&period_end=${p}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData(r.data);
      setTrend(t.data?.trend || []);
      // Auto-switch period
      if (!autoSwitched && r.data?.success && r.data.summary?.linked_channels > 0) {
        const allNoData = (r.data.channels || []).filter(c => c.linked).every(c => c.overall.status === 'no_data');
        if (allNoData) {
          const available = r.data.available_periods || [];
          if (available.length > 0 && available[0] !== p) {
            setAutoSwitched(true);
            setPeriod(available[0]);
          }
        }
      }
    } catch (e) {
      toast.error('Gagal load: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOverview(period); /* eslint-disable-next-line */ }, [period]);

  const filteredChannels = useMemo(() => {
    if (!data?.channels) return [];
    let rows = data.channels;
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter(c =>
        (c.channel_name || '').toLowerCase().includes(s) ||
        (c.owner_name || '').toLowerCase().includes(s) ||
        (c.channel_type || '').toLowerCase().includes(s)
      );
    }
    if (statusFilter !== 'all') {
      rows = rows.filter(c => c.overall.status === statusFilter);
    }
    if (linkedFilter === 'linked') rows = rows.filter(c => c.linked);
    if (linkedFilter === 'unlinked') rows = rows.filter(c => !c.linked);
    return rows;
  }, [data, search, statusFilter, linkedFilter]);

  const goToChannel = (ib_id) => {
    if (!ib_id) return;
    navigate(`/ib/${ib_id}/details?tab=dashboard`);
  };

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6" data-testid="ib-management-dashboard-page">
        <SectionHeader
          icon={Building2}
          title="IB Management Dashboard"
          subtitle="Performance scorecard semua channel — track on-track / off-track / unlinked"
          iconBg="bg-gradient-to-br from-[#10B981] to-[#3B82F6] shadow-[#10B981]/20"
          action={
            <div className="flex items-center gap-2">
              <PeriodSelector value={period} onChange={setPeriod} available={data?.available_periods || []} />
              <Button onClick={() => navigate('/tv-wall')} size="sm"
                data-testid="open-tv-wall-btn"
                className="h-9 bg-gradient-to-r from-[#10B981] to-[#3B82F6] text-white hover:opacity-90">
                <Tv size={14} className="mr-1.5" />TV Wall
              </Button>
              <Button onClick={() => fetchOverview(period)} disabled={loading} size="sm" variant="outline"
                data-testid="refresh-ib-mgmt-btn"
                className="h-9 border-[hsl(var(--border))] hover:border-[hsl(var(--border-hover))]">
                <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
              </Button>
            </div>
          }
        />

        <div className="mt-5 space-y-5 max-w-[1400px]">
          {loading && !data && (
            <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-8 text-center">
              <RefreshCw size={20} className="animate-spin mx-auto text-[#6B7280] mb-2" />
              <p className="text-sm text-[#9CA3AF]">Loading...</p>
            </div>
          )}

          {data?.error === 'no_snapshot' && (
            <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/20 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-[#F59E0B] flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <h3 className="text-white font-semibold mb-1">Scorecard belum di-sync</h3>
                  <p className="text-sm text-[#9CA3AF]">Admin Panel → Scorecard → Sync data dulu.</p>
                </div>
              </div>
            </div>
          )}

          {data?.success && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3">
                <SummaryCard
                  icon={CheckCircle2}
                  label="On Track"
                  value={data.summary?.on_track || 0}
                  total={data.summary?.linked_channels || 0}
                  color={STATUS_META.on_track.color}
                  status="on_track"
                />
                <SummaryCard
                  icon={AlertTriangle}
                  label="At Risk"
                  value={data.summary?.at_risk || 0}
                  total={data.summary?.linked_channels || 0}
                  color={STATUS_META.at_risk.color}
                  status="at_risk"
                />
                <SummaryCard
                  icon={TrendingDown}
                  label="Off Track"
                  value={data.summary?.off_track || 0}
                  total={data.summary?.linked_channels || 0}
                  color={STATUS_META.off_track.color}
                  status="off_track"
                />
              </div>

              {/* Average pct hero + 6-month trend */}
              {data.summary?.average_pct > 0 && (
                <div className="bg-gradient-to-r from-[#10B981]/5 to-[#3B82F6]/5 border border-[hsl(var(--border))] rounded-xl p-4 flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">Average Achievement</p>
                    <p className="text-3xl font-bold text-white">{data.summary.average_pct.toFixed(1)}<span className="text-base text-[#6B7280]">%</span></p>
                    <p className="text-xs text-[#6B7280] mt-1">Across {data.summary.linked_channels} linked channel{data.summary.linked_channels === 1 ? '' : 's'}</p>
                  </div>
                  {trend.length > 0 && (
                    <div className="flex flex-col items-end">
                      <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1">Last 6 months</p>
                      <Sparkline data={trend} width={180} height={40} />
                      <p className="text-[10px] text-[#6B7280] mt-0.5">{trend[0]?.period} → {trend[trend.length - 1]?.period}</p>
                    </div>
                  )}
                  <div className="text-right border-l border-[hsl(var(--border))] pl-4">
                    <p className="text-xs text-[#6B7280]">Period</p>
                    <p className="text-sm text-white font-mono">{data.period}</p>
                  </div>
                </div>
              )}

              {/* Aggregated KPI totals across ALL linked channels */}
              {data.aggregated_kpis && Object.keys(data.aggregated_kpis).length > 0 && (
                <div className="relative bg-gradient-to-br from-[hsl(var(--surface))] via-[hsl(var(--surface))] to-[#10B981]/[0.03] border border-[hsl(var(--border))] rounded-2xl p-5 overflow-hidden" data-testid="aggregated-kpis">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(16,185,129,0.08),_transparent_60%)] pointer-events-none" />
                  <div className="relative flex items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#10B981] to-[#3B82F6] flex items-center justify-center shadow-[0_4px_16px_rgba(16,185,129,0.3)]">
                        <Building2 size={14} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-white text-base font-semibold tracking-tight">Total KPI</h3>
                        <p className="text-[10px] text-[#9CA3AF] mt-0.5">Gabungan {data.summary.linked_channels} channels · {data.period}</p>
                      </div>
                    </div>
                  </div>
                  <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3">
                    {(() => {
                      // Build dynamic list — order CRM-aggregable codes first by templates_meta sequence
                      const metaByCode = Object.fromEntries((data.templates_meta || []).map(t => [t.code, t]));
                      // Show every aggregated KPI we have a value for; fall back to templates_meta order
                      const codes = Object.keys(data.aggregated_kpis || {});
                      // Stable sort by templates_meta order, unknown at end
                      const metaOrder = (data.templates_meta || []).map(t => t.code);
                      codes.sort((a, b) => {
                        const ai = metaOrder.indexOf(a); const bi = metaOrder.indexOf(b);
                        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                      });
                      const COLOR_BY_CAT = {
                        REVENUE: '#10B981', MARKETING: '#3B82F6', OPERATION: '#F59E0B',
                        CREATIVE: '#8B5CF6', SIGNAL: '#F43F5E', SALES: '#06B6D4',
                        GROWTH: '#84CC16', PRODUCT: '#A78BFA', EDUCATION: '#FBBF24',
                        CUSTOMER: '#22D3EE',
                      };
                      const UNIT_PREFIX = { 'MYR': 'RM ', 'RM': 'RM ', 'USD': '$', '$': '$' };
                      const UNIT_SUFFIX = { '%': '%', 'hours': ' h', 'hr': ' h', 'h': ' h' };
                      return codes.map(code => {
                        const v = data.aggregated_kpis[code];
                        const t = (data.aggregated_targets || {})[code] || 0;
                        const meta = metaByCode[code] || {};
                        const numV = Number(v) || 0;
                        const numT = Number(t) || 0;
                        const pct = numT > 0 ? Math.min(numV / numT * 100, 999) : 0;
                        const gap = Math.max(0, numT - numV);
                        const color = COLOR_BY_CAT[meta.category] || '#10B981';
                        const statusColor = numT === 0 ? color : pct >= 80 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#E63946';
                        const prefix = UNIT_PREFIX[meta.unit] || '';
                        const suffix = UNIT_SUFFIX[meta.unit] || '';
                        const label = meta.name || code;
                        const fmt = (x) => `${prefix}${Number(x).toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;
                        return (
                          <div key={code}
                            className="group relative bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4 overflow-hidden hover:border-[hsl(var(--border-hover))] hover:-translate-y-0.5 transition-all"
                            data-testid={`agg-kpi-${code}`}>
                            <div className="absolute inset-x-0 top-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${color}, transparent)` }} />
                            <div className="absolute -right-6 -top-6 w-20 h-20 rounded-full blur-2xl opacity-20 group-hover:opacity-40 transition-opacity" style={{ backgroundColor: color }} />
                            <div className="relative">
                              <div className="flex items-center gap-1.5 mb-2">
                                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                                <p className="text-[10px] text-[#9CA3AF] uppercase tracking-[0.08em] font-medium">{label}</p>
                              </div>
                              <p className="text-2xl font-bold text-white tabular-nums leading-tight">{fmt(numV)}</p>
                              {numT > 0 ? (
                                <>
                                  <p className="text-[10px] text-[#6B7280] mt-1">
                                    of <span className="text-[#9CA3AF] font-medium">{fmt(numT)}</span>
                                  </p>
                                  <div className="mt-2 w-full bg-[hsl(var(--surface))] rounded-full h-1.5 overflow-hidden">
                                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(pct, 100)}%`, background: `linear-gradient(90deg, ${statusColor}, ${statusColor}cc)` }} />
                                  </div>
                                  <div className="flex items-center justify-between mt-1.5 text-[10px]">
                                    <span style={{ color: statusColor }} className="font-bold tabular-nums">{Math.min(pct, 100).toFixed(1)}%</span>
                                    {gap > 0 && <span className="text-[#6B7280]">gap: {fmt(gap)}</span>}
                                  </div>
                                </>
                              ) : (
                                <p className="text-[9px] text-[#6B7280] mt-2 italic">No target set</p>
                              )}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* Leaderboard: Top / Bottom performers */}
              {((data.top_performers?.length || 0) > 0 || (data.bottom_performers?.length || 0) > 0) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.top_performers?.length > 0 && (
                    <div className="bg-[hsl(var(--surface))] border border-[#10B981]/20 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 rounded-md bg-[#10B981]/15 flex items-center justify-center"><CheckCircle2 size={12} className="text-[#10B981]" /></div>
                        <h3 className="text-white text-sm font-semibold">Top Performers</h3>
                      </div>
                      <div className="space-y-2">
                        {data.top_performers.map((c, idx) => (
                          <div key={c.ib_id} onClick={() => goToChannel(c.ib_id)} data-testid={`top-perf-${idx}`}
                            className="flex items-center gap-2 cursor-pointer hover:bg-[hsl(var(--surface-elevated))] rounded-md px-2 py-1.5 transition">
                            <div className="w-6 h-6 rounded-full bg-[#10B981]/15 text-[#10B981] flex items-center justify-center text-xs font-bold">{idx + 1}</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-xs font-semibold truncate">{c.channel_name}</p>
                              <p className="text-[10px] text-[#6B7280]">{c.staff_count} staff</p>
                            </div>
                            <p className="text-[#10B981] font-bold text-sm">{Math.min(c.overall.overall_pct, 100).toFixed(1)}%</p>
                            <ChevronRight size={12} className="text-[#6B7280]" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {data.bottom_performers?.length > 0 && (
                    <div className="bg-[hsl(var(--surface))] border border-[#E63946]/20 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 rounded-md bg-[#E63946]/15 flex items-center justify-center"><TrendingDown size={12} className="text-[#E63946]" /></div>
                        <h3 className="text-white text-sm font-semibold">Needs Attention</h3>
                      </div>
                      <div className="space-y-2">
                        {data.bottom_performers.map((c, idx) => (
                          <div key={c.ib_id} onClick={() => goToChannel(c.ib_id)} data-testid={`bottom-perf-${idx}`}
                            className="flex items-center gap-2 cursor-pointer hover:bg-[hsl(var(--surface-elevated))] rounded-md px-2 py-1.5 transition">
                            <div className="w-6 h-6 rounded-full bg-[#E63946]/15 text-[#E63946] flex items-center justify-center text-xs font-bold">!</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-xs font-semibold truncate">{c.channel_name}</p>
                              <p className="text-[10px] text-[#6B7280]">{c.overall.off_track_count} off-track metric</p>
                            </div>
                            <p className="text-[#E63946] font-bold text-sm">{Math.min(c.overall.overall_pct, 100).toFixed(1)}%</p>
                            <ChevronRight size={12} className="text-[#6B7280]" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Filters */}
              <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-3 flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search channel, owner, type..."
                    data-testid="ib-mgmt-search"
                    className="pl-9 bg-transparent border-[hsl(var(--border))] text-white text-sm h-9"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter size={14} className="text-[#6B7280]" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="ib-mgmt-status-filter"
                    className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-3 h-9"
                  >
                    <option value="all">All Status</option>
                    <option value="on_track">On Track</option>
                    <option value="at_risk">At Risk</option>
                    <option value="off_track">Off Track</option>
                    <option value="no_data">No Data</option>
                  </select>
                  <select
                    value={linkedFilter}
                    onChange={(e) => setLinkedFilter(e.target.value)}
                    data-testid="ib-mgmt-linked-filter"
                    className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-sm rounded-md px-3 h-9"
                  >
                    <option value="all">All Channels</option>
                    <option value="linked">Linked Only</option>
                    <option value="unlinked">Unlinked Only</option>
                  </select>
                </div>
              </div>

              {/* Channel grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredChannels.length === 0 && (
                  <div className="col-span-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-8 text-center">
                    <HelpCircle size={20} className="mx-auto text-[#6B7280] mb-2" />
                    <p className="text-sm text-[#9CA3AF]">Tiada channel match filter awak.</p>
                  </div>
                )}
                {filteredChannels.map(c => {
                  const meta = STATUS_META[c.overall.status] || STATUS_META.no_data;
                  const avatarUrl = c.brand_logo_url || c.personal_image_url;
                  const initials = (c.channel_name || c.ib_name || '?')
                    .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
                  // Accent color drawn from status (gives each card a status-aware glow)
                  const accentMap = { on_track: '#10B981', at_risk: '#F59E0B', off_track: '#E63946', no_data: '#6B7280' };
                  const accent = accentMap[c.overall.status] || '#10B981';
                  return (
                    <div key={c.ib_id}
                      data-testid={`ib-mgmt-channel-card-${c.ib_id}`}
                      className="group relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-2xl overflow-hidden hover:border-[hsl(var(--border-hover))] hover:-translate-y-0.5 transition-all duration-300">
                      {/* Top accent gradient strip */}
                      <div className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${accent}, ${accent}66, transparent)` }} />
                      {/* Subtle glow */}
                      <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-15 group-hover:opacity-30 transition-opacity" style={{ backgroundColor: accent }} />
                      <div className="relative p-4">
                        <div className="flex items-start gap-3 mb-3">
                          {/* Avatar */}
                          <div className="relative w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 ring-1 ring-[hsl(var(--border))]" style={{ background: `linear-gradient(135deg, ${accent}22, ${accent}11)` }}>
                            {avatarUrl ? (
                              <img src={avatarUrl} alt={c.channel_name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white font-bold text-sm" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}80)` }}>
                                {initials}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="text-white text-sm font-semibold truncate">{c.channel_name || c.ib_name}</p>
                              {!c.linked && (
                                <span className="inline-flex items-center gap-0.5 bg-[#6B7280]/15 border border-[#6B7280]/30 text-[#9CA3AF] text-[9px] px-1.5 py-0.5 rounded-full">
                                  <Link2Off size={9} />Unlinked
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-[#6B7280] truncate mt-0.5">
                              {c.channel_owner || c.owner_name || c.channel_type || '—'}
                              {c.tagline ? <span className="ml-1 italic">· {c.tagline}</span> : ''}
                            </p>
                          </div>
                        </div>

                        {c.linked ? (
                          <>
                            <div className="flex items-end justify-between mb-3">
                              <div>
                                <p className="text-[9px] text-[#6B7280] uppercase tracking-wider mb-0.5">Overall</p>
                                <p className={`text-3xl font-bold tracking-tight ${meta.text}`}>
                                  {Math.min(c.overall.overall_pct, 100).toFixed(1)}<span className="text-base text-[#6B7280]">%</span>
                                </p>
                              </div>
                              <StatusBadge status={c.overall.status} />
                            </div>
                            <ProgressBar pct={c.overall.overall_pct} status={c.overall.status} />
                            <div className="flex items-center justify-between mt-3 text-[10px]">
                              <span className="text-[#6B7280] font-medium">{c.staff_count} staff · {c.overall.templates_count} metric</span>
                              <span className="flex items-center gap-1">
                                <span className="inline-flex items-center gap-0.5 text-[#10B981]"><span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />{c.overall.on_track_count}</span>
                                <span className="inline-flex items-center gap-0.5 text-[#F59E0B]"><span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />{c.overall.at_risk_count}</span>
                                <span className="inline-flex items-center gap-0.5 text-[#E63946]"><span className="w-1.5 h-1.5 rounded-full bg-[#E63946]" />{c.overall.off_track_count}</span>
                              </span>
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); goToChannel(c.ib_id); }}
                              data-testid={`view-details-${c.ib_id}`}
                              className="mt-3 w-full flex items-center justify-center gap-1.5 bg-gradient-to-r from-[hsl(var(--surface-elevated))] to-[hsl(var(--surface-elevated))] hover:from-[#10B981]/20 hover:to-[#3B82F6]/20 border border-[hsl(var(--border))] hover:border-[#10B981]/40 text-[#9CA3AF] hover:text-white py-2 rounded-lg text-xs font-semibold transition-all">
                              View Details
                              <ChevronRight size={12} />
                            </button>
                          </>
                        ) : (
                          <div className="bg-[#6B7280]/5 border border-dashed border-[#6B7280]/30 rounded-lg p-3 mt-2">
                            <p className="text-[10px] text-[#9CA3AF]">Channel belum di-link ke Scorecard. Pastikan nama channel match dengan IB node description, atau set Scorecard Alias.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </ResponsiveLayout>
  );
}
